'use client';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createTextNode,
  $getRoot,
  $isElementNode,
  COMMAND_PRIORITY_HIGH,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  type LexicalEditor,
} from 'lexical';
import { useEffect } from 'react';
import type {
  MessageHistoryEntry,
  MessageHistoryMentionSnapshot,
} from '../hooks/createMessageHistoryController';
import type { MentionEntityWithOffset } from './buildMessageSegments';
import type { MessageHistoryNavigation } from '../hooks/useMessageHistory';
import { isSelectionAtDocumentStart } from './messageHistorySelection';
import {
  normalizeSerializedEditorStateForHistory,
  removeEmptyEdgeParagraphs,
  resetEditorToEmptyParagraph,
} from './editorParagraphNormalization';

type MessageHistoryMentionSnapshotInput = {
  mentions: MessageHistoryMentionSnapshot[];
  editorMentions: MentionEntityWithOffset[];
};

type MessageHistoryPluginProps = {
  messageHistory: MessageHistoryNavigation;
  isDisabled?: boolean;
  getMentionSnapshot: () => MessageHistoryMentionSnapshotInput;
  onEntryRestored: (entry: MessageHistoryEntry) => void;
};

function getCurrentEntry({
  editor,
  mentions,
  editorMentions,
}: MessageHistoryMentionSnapshotInput & {
  editor: LexicalEditor;
}): MessageHistoryEntry {
  const editorStateJson = normalizeSerializedEditorStateForHistory(
    JSON.stringify(editor.getEditorState().toJSON()),
  );
  const plainText = editor
    .getEditorState()
    .read(() => $getRoot().getTextContent());
  return { editorStateJson, plainText, mentions, editorMentions };
}

function setEditorPlainText({
  text,
  editor,
}: {
  text: string;
  editor: LexicalEditor;
}) {
  editor.update(() => {
    resetEditorToEmptyParagraph();
    const root = $getRoot();
    const paragraph = root.getFirstChild();
    if ($isElementNode(paragraph)) {
      paragraph.append($createTextNode(text));
      paragraph.selectEnd();
    }
  });
  editor.focus();
}

function restoreEditorEntry({
  entry,
  editor,
}: {
  entry: MessageHistoryEntry;
  editor: LexicalEditor;
}) {
  try {
    const editorState = editor.parseEditorState(
      normalizeSerializedEditorStateForHistory(entry.editorStateJson),
    );
    editor.setEditorState(editorState);
    editor.update(() => {
      removeEmptyEdgeParagraphs();
      const root = $getRoot();
      const lastChild = root.getLastChild();
      if ($isElementNode(lastChild)) {
        lastChild.selectEnd();
      }
    });
    editor.focus();
  } catch (error: unknown) {
    console.warn('[MessageHistoryPlugin] Failed to restore editor state', {
      error: error instanceof Error ? error : new Error(String(error)),
    });
    setEditorPlainText({ text: entry.plainText, editor });
  }
}

function scheduleRestoreEditorEntry({
  entry,
  editor,
  onEntryRestored,
}: {
  entry: MessageHistoryEntry;
  editor: LexicalEditor;
  onEntryRestored: (entry: MessageHistoryEntry) => void;
}) {
  setTimeout(() => {
    restoreEditorEntry({ entry, editor });
    onEntryRestored(entry);
  }, 0);
}

export function MessageHistoryPlugin({
  messageHistory,
  isDisabled,
  getMentionSnapshot,
  onEntryRestored,
}: MessageHistoryPluginProps) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (isDisabled) {
      return;
    }

    const unregisterUp = editor.registerCommand(
      KEY_ARROW_UP_COMMAND,
      (event) => {
        const isBrowsingHistory = messageHistory.isBrowsingHistory();
        const atDocumentStart = editor
          .getEditorState()
          .read(() => isSelectionAtDocumentStart());
        if (!atDocumentStart && !isBrowsingHistory) {
          return false;
        }

        const mentionSnapshot = getMentionSnapshot();
        const currentEntry = getCurrentEntry({ editor, ...mentionSnapshot });
        const previousMessage = messageHistory.navigateUp({ currentEntry });
        if (previousMessage === null) {
          return false;
        }

        event?.preventDefault();
        scheduleRestoreEditorEntry({
          entry: previousMessage,
          editor,
          onEntryRestored,
        });
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterDown = editor.registerCommand(
      KEY_ARROW_DOWN_COMMAND,
      (event) => {
        if (!messageHistory.isBrowsingHistory()) {
          return false;
        }

        const mentionSnapshot = getMentionSnapshot();
        const currentEntry = getCurrentEntry({ editor, ...mentionSnapshot });
        const nextMessage = messageHistory.navigateDown({ currentEntry });
        if (nextMessage === null) {
          return false;
        }

        event?.preventDefault();
        scheduleRestoreEditorEntry({
          entry: nextMessage,
          editor,
          onEntryRestored,
        });
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );

    return () => {
      unregisterUp();
      unregisterDown();
    };
  }, [editor, getMentionSnapshot, isDisabled, messageHistory, onEntryRestored]);

  return null;
}
